import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Observable, Subscription, catchError, of } from 'rxjs';

import { ChatResponsesComponent } from '../chat-responses/chat-responses.component';
import { ChatMessengerService } from '../chat-messenger.service';
import { ChatToolPlaceholderComponent } from '../chat-tool-placeholder/chat-tool-placeholder.component';
import { parsePhoneNumber } from 'libphonenumber-js';
import { GenesysCloudService } from '../../genesys-cloud.service';
import {
  SearchStudentRequest,
  StudentInfo,
  StudentService,
  SmsSendRequested,
  PhoneInfo,
  GetSmsHistoryRequest,
  SmsHistoryService,
  SmsHistory,
  GetSmsMessagesRequest,
  SmsService,
  SmsMessage,
  UserService,
  CapStatisticsService,
  GetCapStatisticsRequest,
  CapStatistics,
  GetRoleConfigurationResponse,
  SmsComplianceResponse,
  SmsPickUpRequested,
  GetPhoneInfoRequest
} from '../../../lib';
import { StudentSelectedBroadcastService } from '../../student-selected-broadcast.service';
import { ToastService } from '../../toast/toast.service';
import { AllFilter, ChatFilter, ChatFilterComponent, FilterType } from '../chat-filter/chat-filter.component';
import { PhoneService } from '../../phone.service';
import { Models } from 'purecloud-platform-client-v2';
import { AppConfigService } from '../../app-config.service';


enum ActiveToolbarButton {
  filter,
  script,
  journey,
  responses,
  notes,
  wrapup
}

interface ToolBoxItem {
  button: ActiveToolbarButton,
  component: any,
  inputs: any
}

const MAX_INTEGER = 2147483647;
const NONE: string = 'none';
const RESPONSEMANAGEMENTONLY: string = 'responsemanagementonly';
const FREETEXT: string = 'freetext';
const NO_CONVERSATION_SELECTED = -1;
const CONVERSATION_INDEX_NEEDS_REFRESH = -2;

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})

export class ChatComponent implements OnInit, OnDestroy, AfterViewInit {
  private emailTopicSubscription: Subscription | undefined;
  conversations: SmsHistory[] = [];
  conversationLastRead: Map<string, Date> = new Map();
  activeConversationIndex: number = NO_CONVERSATION_SELECTED;
  messages: SmsMessage[] = [];
  filteredMessages: SmsMessage[] = [];
  isLoading: boolean = false;
  activeToolbarButton: ActiveToolbarButton = ActiveToolbarButton.responses;
  selectedToolIndex: number = Object.values(ActiveToolbarButton).indexOf('responses');
  ATB: any = ActiveToolbarButton;
  msgAddedObserver!: MutationObserver;
  msgContainer: HTMLElement | null = null;
  atLastMessage: boolean = true;
  msgText: string = '';
  searchResults: StudentInfo[] = [];
  searchType: string = 'SyStudentId';
  searchValue: string = '';
  searchErrorMessage: string | null = null;
  activeStudent: StudentInfo = {};
  activeStudentCapStatistics: CapStatistics = {};
  userInfo: GetRoleConfigurationResponse = {};
  allowedSchoolStatuses: string[] = [];
  smsCompliance: SmsComplianceResponse = {};
  allowedToSend: boolean = false;
  cannedMessagesOnly: boolean = false;
  defaultMessageBoxPlaceholder: string = 'Enter message...';
  messageBoxPlaceholder: string = this.defaultMessageBoxPlaceholder;
  @ViewChild('msgTextBox') msgTextBox: ElementRef | undefined;
  activeConversationId: string | undefined;
  chatFilter: ChatFilter = AllFilter;
  chatFilterSubscription!: Subscription;
  enableSearch = false;
  INBOUND: number = 0;
  OUTBOUND: number = 1;

  toolboxItems: ToolBoxItem[] = [
    {
      button: ActiveToolbarButton.filter,
      component: ChatFilterComponent,
      inputs: {}
    },
    {
      button: ActiveToolbarButton.script,
      component: ChatToolPlaceholderComponent,
      inputs: { toolName: 'Script' }
    },
    {
      button: ActiveToolbarButton.journey,
      component: ChatToolPlaceholderComponent,
      inputs: { toolName: 'Journey' }
    },
    {
      button: ActiveToolbarButton.responses,
      component: ChatResponsesComponent,
      inputs: {}
    },
    {
      button: ActiveToolbarButton.notes,
      component: ChatToolPlaceholderComponent,
      inputs: { toolName: 'Notes' }
    },
    {
      button: ActiveToolbarButton.wrapup,
      component: ChatToolPlaceholderComponent,
      inputs: { toolName: 'Wrapup' }
    }
  ];
  iDashUrl: string | undefined;
  private genesysUserId! : string;
  public version: string = '';
  
  constructor(
    private messenger: ChatMessengerService,
    private studentService: StudentService,
    private smsHistoryService: SmsHistoryService,
    private smsService: SmsService,
    private userService: UserService,
    private capStatisticsService: CapStatisticsService,
    private route: ActivatedRoute,
    private location: Location,
    private genesysCloudService: GenesysCloudService,
    private studentSelectedBroadCastService: StudentSelectedBroadcastService,
    private toastService: ToastService,
    private phoneService: PhoneService,
    private appConfigService: AppConfigService,
  ) { 
    this.genesysUserId = localStorage.getItem('genesysUserId') || 'NOT SET';
    this.version = appConfigService.config?.version ?? '';
  }

  ngAfterViewInit(): void {
    this.subscribeToNotifications();
  }

  ngOnInit(): void {
    this.studentSelectedBroadCastService.startConnection()
    this.studentSelectedBroadCastService.messageReceived$.subscribe(message => {
      if (message) {
        let selectedStudent = JSON.parse(message)
        this.loadSpecifiedStudent(selectedStudent.SyStudentId)
      }
    });

    this.messenger.responseText.subscribe(response => {
      if (this.allowedToSend) {
        this.msgText += response;
      }
    });

    this.getUserInfo();
    this.subscribeToNotifications();
    this.getConversationHistory().subscribe(_ => {
      this.initilizeConversationLastReadMap();

      const syStudentId = Number(this.route.snapshot.paramMap.get('syStudentId'));
      if (syStudentId) {
        this.loadSpecifiedStudent(syStudentId);
      }
    });

    this.msgContainer = document.getElementById("MessageContainer");

    this.msgAddedObserver = new MutationObserver((mutationList: MutationRecord[], observer: MutationObserver) => {
      mutationList.forEach(() => {
        if (this.msgContainer) {
          if (!this.checkIfAtLastMessge()) {
            this.scrollToLatestMessage();
          }
        }
      });
    });

    if (this.msgContainer) {
      const config = { childList: true };
      this.msgAddedObserver.observe(this.msgContainer, config);

      this.msgContainer.addEventListener('scroll', () => {
        this.atLastMessage = this.checkIfAtLastMessge();
      });
    }

    this.chatFilterSubscription = this.messenger.chatFilter.subscribe(filter => {
      this.chatFilter = filter;
      this.filterMessages();
    });
  }

  ngOnDestroy(): void {
    this.msgAddedObserver.disconnect();
    this.emailTopicSubscription?.unsubscribe();
    this.chatFilterSubscription.unsubscribe();
  }

  initilizeConversationLastReadMap(): void {
    this.conversations.forEach(conversation => {
      this.updateConversationLastRead(conversation);
    });
  }

  updateConversationLastRead(conversation: SmsHistory): void {
    if (conversation.phoneNumber && conversation.timeStamp) {
      const key = this.getConversationLastReadKey(conversation);
      let lastReadDate = new Date(conversation.timeStamp);
      this.conversationLastRead.set(key, lastReadDate);
    }
  }

  hasUnreadMessages(conversation: SmsHistory): boolean {
    if (conversation.phoneNumber && conversation.timeStamp) {
      const key = this.getConversationLastReadKey(conversation);
      const lastReadDate = this.conversationLastRead.get(key);
      const conversationTimeStamp = new Date(conversation.timeStamp);
      if (lastReadDate) {
        return conversationTimeStamp > lastReadDate;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  getConversationLastReadKey(conversation: SmsHistory): string {
    const phoneNumber = conversation.phoneNumber ? conversation.phoneNumber : '';
    const syStudentId = conversation.syStudentId ? conversation.syStudentId.toString() : 'NoId';
    return phoneNumber + ':' + syStudentId;
  }

  filterMessages(): void {
    switch (this.chatFilter.type) {
      case FilterType.all:
        this.filteredMessages = this.messages;
        break;
      case FilterType.workgroupQueue:
        this.filteredMessages = this.messages.filter(msg => msg.workgroupQueue === this.chatFilter.value);
        break;
      case FilterType.agentName:
        this.filteredMessages = this.messages.filter(msg => msg.agentName === this.chatFilter.value);
        break;
    }
  }

  getUserInfo(): void {
    this.userService.apiSmsPermissionGet().pipe(
      catchError(error => {
        this.toastService.show({ body: 'Server error on getting SMS permissions', classname: 'bg-danger text-light', delay: 5000, header: 'SMS Permission Failed' });
        return [];
      })
    ).subscribe(resp => {
      this.userInfo = resp;
      if (this.userInfo.AllowedSchoolStatuses) {
        this.allowedSchoolStatuses = this.userInfo.AllowedSchoolStatuses?.split(',').map(status => status.trim().toLowerCase());
      }
    })
  }

  getConversationHistory(backgroundRefresh: boolean = false): Observable<void> {
    return new Observable<void>(obs => {
      if (!backgroundRefresh) {
        this.isLoading = true;
      }
      const req: GetSmsHistoryRequest = {
        user: ''
      }

      this.smsHistoryService.apiSmsHistoryPost(req).pipe(
        catchError(error => {
          if (!backgroundRefresh) {
            this.isLoading = false;
          }
          console.error(`apiSmsHistoryPost error: ${JSON.stringify(error)}`);
          this.toastService.show({ body: 'Server error on getting SMS history', classname: 'bg-danger text-light', delay: 5000, header: 'SMS history Failed' });
          obs.next();
          obs.complete();
          return [];
        })
      ).subscribe(resp => {
        if (resp.value) {
          this.conversations = resp.value;
          if (this.activeConversationIndex !== NO_CONVERSATION_SELECTED) {
            this.activeConversationIndex = this.getConversationIndex(this.activeStudent);
            this.updateConversationLastRead(this.conversations[this.activeConversationIndex]);
          }
        }

        if (!backgroundRefresh) {
          this.isLoading = false;
        }
        obs.next();
        obs.complete();
      });
    });
  }

  loadSpecifiedStudent(syStudentId: number): void {
    const conversationIndex = this.conversations.findIndex(conv => conv.syStudentId === syStudentId);
    if (conversationIndex !== -1) {
      this.openConversation(conversationIndex);
    } else {
      this.lookupStudent({ syStudentId: syStudentId }).subscribe(response => {
        if (response.length > 0) {
          this.displaySelectedStudent(response[0]);
        }
      });
    }

    this.location.replaceState('/sms');
    this.msgText = '';
  }

  openConversation(conversationIndex: number): void {
    if (this.activeConversationIndex === conversationIndex) {
      return;
    }

    this.isLoading = true;

    this.updateConversationLastRead(this.conversations[conversationIndex]);

    this.getUserInfo();

    this.activeConversationIndex = conversationIndex;
    const conversation: SmsHistory = this.conversations[conversationIndex];

    let studentLookup$: Observable<StudentInfo[]>;

    if (conversation.syStudentId) {
      const req: SearchStudentRequest = {
        syStudentId: conversation.syStudentId,
        phoneNumber: conversation.phoneNumber
      }
      studentLookup$ = this.lookupStudent(req);
    } else {
      const noStudent: StudentInfo = {
        phone: { phone: conversation.phoneNumber }
      }
      studentLookup$ = of([noStudent]);
    }

    studentLookup$.subscribe(response => {
      this.setActiveStudent(response[0]);
      this.getMessages().subscribe(_ => {
        this.initFiltersAndSetFocus();
      });
    });
  }

  initFiltersAndSetFocus(): void {
    this.messenger.setChatFilter(AllFilter);
    this.messenger.setSmsMessages(this.messages);
    if (this.allowedToSend) {
      this.msgTextBox?.nativeElement.focus();
    }
  }

  getMessages(backgroundRefresh: boolean = false): Observable<void> {
    if (!backgroundRefresh) {
      this.isLoading = true;
    }

    return new Observable<void>(obs => {
      const req: GetSmsMessagesRequest = {
        phone: this.activeStudent.phone?.phone,
        syStudentId: this.activeStudent.syStudentId,
        pageNumber: 1,
        pageSize: MAX_INTEGER
      }

      this.smsService.apiSmsMessagesPost(req).subscribe({
        next: (resp) => {
          if (resp.value) {
            this.messages = resp.value;
            this.filterMessages();
            if (!backgroundRefresh) {
              this.isLoading = false;
            }
            this.checkInitiateAndRespondPermissions();
            obs.next();
            obs.complete();
          }
        },
        error: (error) => {
          if (!backgroundRefresh) {
            this.isLoading = false;
          }
          this.toastService.show({ body: 'Server error reading SMS history', classname: 'bg-danger text-light', delay: 5000, header: 'SMS History Failed' });
          obs.next();
          obs.complete();
        }
      });
    });
  }

  setActiveStudent(student: StudentInfo): void {
    this.activeStudent = student;
    this.msgText = '';
    if (!this.activeStudent.firstName && !this.activeStudent.lastName) {
      this.activeStudent.firstName = "No";
      this.activeStudent.lastName = "Name";
    }

    this.messenger.setActiveStudentId(this.activeStudent.syStudentId)

    this.getCaps();
    this.checkSchoolAndDncStatus();
    this.getActiveConversationId();
    if (this.activeStudent.syStudentId) {
      this.iDashUrl = this.userInfo.IDashURL?.replace("{STUDENT_ID}", this.activeStudent.syStudentId.toString());
    }
  }

  getCaps(refreshingStudentCaps: boolean = false): void {
    if (!refreshingStudentCaps) {
      this.activeStudentCapStatistics = {};
    }

    if (this.activeStudent.phone?.phone) {
      let req: GetCapStatisticsRequest = {
        phoneNumber: this.activeStudent.phone.phone,
        syStudentId: this.activeStudent.syStudentId,
      }

      this.capStatisticsService.apiSmsCapStatisticsPost(req).pipe(
        catchError(error => {
          console.error(`apiSmsCapStatisticsPost error: ${JSON.stringify(error)}`);
          this.toastService.show({ body: 'Server error on getting Cap information.', classname: 'bg-danger text-light', delay: 5000, header: 'SMS Cap Failed' });
          return [];
        })
      ).subscribe(resp => {
        this.activeStudentCapStatistics = resp;
      })
    }
  }

  checkSchoolAndDncStatus(): void {
    this.allowedToSend = true;
    this.messageBoxPlaceholder = this.defaultMessageBoxPlaceholder;
    if (!this.activeStudent.syStudentId && this.userInfo.SendToPhonePermission?.toLowerCase() !== 'any') {
      this.allowedToSend = false;
      this.messageBoxPlaceholder = 'You do not have permission to send messages direct to phone numbers';
    }

    if (this.allowedToSend && this.allowedSchoolStatuses.length > 0 && this.activeStudent.schoolStatus) {
      if (!this.validateSchoolStatus(this.activeStudent.schoolStatus)) {
        this.allowedToSend = false;
        this.messageBoxPlaceholder = `Unable to text because student status is ${this.activeStudent.schoolStatus}`;
      }
    }

    if (this.allowedToSend && this.activeStudent.phone?.isSmsDNC) {
      this.allowedToSend = false;
      this.messageBoxPlaceholder = 'This phone number is Do Not Contact';
    }
  }

  validateSchoolStatus(studentSchoolStatus: string): boolean {
    return this.allowedSchoolStatuses.includes('any') || this.allowedSchoolStatuses.includes(studentSchoolStatus.toLowerCase());
  }

  checkInitiateAndRespondPermissions(): void {
    if (this.allowedToSend) {
      if (this.messages.length > 0) {
        switch (this.userInfo.RespondPermission?.toLowerCase()) {
          case NONE:
            this.allowedToSend = false;
            this.messageBoxPlaceholder = 'You do not have permission to respond to SMS';
            break;
          case RESPONSEMANAGEMENTONLY:
            this.cannedMessagesOnly = true;
            this.messageBoxPlaceholder = 'You may only respond with canned responses.';
            break;
          case FREETEXT:
            this.cannedMessagesOnly = false;
            this.messageBoxPlaceholder = this.defaultMessageBoxPlaceholder;
            break;
        }
      } else {
        switch (this.userInfo.InitiatePermission?.toLowerCase()) {
          case NONE:
            this.allowedToSend = false;
            this.messageBoxPlaceholder = 'You do not have permission to initiate SMS';
            break;
          case RESPONSEMANAGEMENTONLY:
            this.cannedMessagesOnly = true;
            this.messageBoxPlaceholder = 'You may only initiate SMS with canned responses.';
            break;
          case FREETEXT:
            this.cannedMessagesOnly = false;
            this.messageBoxPlaceholder = this.defaultMessageBoxPlaceholder;
            break;
        }
      }
    }
  }

  getActiveConversationId(): void {
    this.activeConversationId = undefined;

    this.genesysCloudService.getEmailConversations().subscribe(conversations => {
      const conversationIndex = conversations.entities?.findIndex((conv: any) => {
        return conv.participants.some((participant: any) => {
          return (
            participant.purpose === 'customer' &&
            participant.attributes.PhoneNumber === this.activeStudent.phone?.phone &&
            (this.activeStudent.syStudentId === undefined || this.activeStudent.syStudentId === parseInt(participant.attributes.SyStudentId, 10))
          )
        }
        );
      });

      if (conversationIndex !== undefined && conversationIndex !== -1 && conversations.entities) {
        this.activeConversationId = conversations.entities[conversationIndex].id;
      }
    });
  }

  toolbarButtonClicked(selectedButton: ActiveToolbarButton): void {
    this.activeToolbarButton = selectedButton;

    const buttonIndex = this.toolboxItems.findIndex(item => item.button === selectedButton);
    if (buttonIndex !== -1) {
      if (this.toolboxItems[buttonIndex].button === ActiveToolbarButton.filter) {
        this.messenger.setChatFilter(this.chatFilter);
        this.messenger.setSmsMessages(this.messages);
      }
      this.selectedToolIndex = buttonIndex;
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if(!this.allowedToSend) return;
    if (event.ctrlKey && event.key === 'Enter') {
      this.sendButtonPressed();
    }
  }

  sendButtonPressed(): void {
    this.sendMessage().subscribe(_ => {
      this.msgText = '';
    });
  }

  disconnectButtonPressed(): void {
    if (this.activeConversationId) {
      this.smsService.apiSmsDisconnectPost({ conversationId: this.activeConversationId }).pipe(
        catchError(error => {
          console.error(`apiSmsDisconnectPost error: ${JSON.stringify(error)}`);
          this.toastService.show({ body: 'Server error on disconnecting SMS', classname: 'bg-danger text-light', delay: 5000, header: 'SMS Disconnect Failed' });
          return [];
        })
      ).subscribe(_ => {
        this.toastService.show({ body: 'Conversation disconnected successfully.', classname: 'bg-success text-light', delay: 5000, header: 'Conversation Disconnected' });
        this.getActiveConversationId();
      });
    }
  }

  sendMessage(): Observable<void> {
    let req: SmsSendRequested;

    if (this.msgText.length === 0) {
      return of();
    } else if (this.activeStudent.phone?.phone) {
      req = {
        phoneNumber: this.activeStudent.phone.phone,
        syStudentId: this.activeStudent.syStudentId,
        body: this.msgText
      }

      return new Observable<void>(obs => {
        this.isLoading = true;
        if (this.activeConversationIndex === NO_CONVERSATION_SELECTED) {
          this.activeConversationIndex = CONVERSATION_INDEX_NEEDS_REFRESH;
        }
        this.smsService.apiSmsRequestedPost(req)
          .pipe(
            catchError(error => {
              console.error(`apiSmsRequestedPost error sending request ${JSON.stringify(req)}:\n${JSON.stringify(error)}`);
              this.toastService.show({ body: 'Server error on Sending SMS', classname: 'bg-danger text-light', delay: 5000, header: 'SMS Failed' });
              this.isLoading = false;
              obs.next();
              obs.complete();
              return [];
            })
          )
          .subscribe(res => {
            if(res.error) {
               this.toastService.show({ body: 'Cannot Send SMS ' + res.error, classname: 'bg-danger text-light', delay: 5000, header: 'SMS Send Failed' });
            }
            this.isLoading = false;
            this.msgText = '';
            this.getConversationHistory().subscribe();
            this.getMessages().subscribe();
            this.getCaps(true);
            obs.next();
            obs.complete();

          });
      });
    } else {
      return of();
    }
  }

  getMsgID(index: number): string {
    return `MessageID-${index}`;
  }

  checkIfAtLastMessge(): boolean {
    if (this.msgContainer && (this.msgContainer?.scrollHeight - this.msgContainer?.scrollTop - this.msgContainer?.clientHeight < 1)) {
      return true;
    } else {
      return false;
    }
  }

  scrollToLatestMessage(): void {
    const lastIndex = this.filteredMessages.length;
    if (lastIndex) {
      const lastMsgId = this.getMsgID(lastIndex - 1);
      const lastMsg = document.getElementById(lastMsgId);
      if (lastMsg) {
        lastMsg.scrollIntoView(true);
      }
    }
  }

  performStudentSearch(): void {
    let req: SearchStudentRequest = {};

    if (this.searchType === 'SyStudentId') {
      req.syStudentId = parseInt(this.searchValue, 10);
    } else {
      req.phoneNumber = this.searchValue;
    }

    this.isLoading = true;
    this.lookupStudent(req).subscribe(response => {
      this.searchResults = response;
      if (response.length === 0) {
        if (this.searchType === 'Phone Number' && this.userInfo.SendToPhonePermission?.toLowerCase() === 'any') {
          const phoneInfoReq: GetPhoneInfoRequest = {
            phone: this.searchValue
          }

          this.smsService.apiSmsGetPhoneInfoPost(phoneInfoReq).pipe(
            catchError(error => {
              console.error(`apiSmsGetPhoneInfoPost error sending request ${JSON.stringify(phoneInfoReq)}:\n${JSON.stringify(error)}`);
              this.toastService.show({ body: 'Server error on sending Phone Info request', classname: 'bg-danger text-light', delay: 5000, header: 'Phone Info Request Failed' });
              return [];
            })
          ).subscribe(resp => {
            this.isLoading = false;
            const student: StudentInfo = {
              firstName: 'No',
              lastName: 'Name',
              schoolStatus: 'School status unknown',
              campus: 'Campus unknown',
              phone: resp
            }
            this.searchResults.push(student);
          });
        } else {
          this.isLoading = false;
          this.searchErrorMessage = 'No student found with that ' + this.searchType;
        }
      } else this.isLoading = false;
    });
  }

  lookupStudent(req: SearchStudentRequest): Observable<StudentInfo[]> {
    this.searchErrorMessage = null;
    this.searchResults = [];

    return this.studentService.apiStudentPost(req).pipe(
      catchError(error => {
        this.isLoading = false;
        console.error(`apiStudentPost error: ${JSON.stringify(error)}`);
        this.searchErrorMessage = 'There was an error searching for the student.';
        return [];
      })
    );
  }

  validateSearchInputKeys(event: KeyboardEvent): boolean {
    if (event.key === 'Enter' && this.enableSearch) {
      this.performStudentSearch();
    }
    const charCode = event.key.charCodeAt(0);
    switch(this.searchType) {
      case 'Phone Number':
        if (!this.isNumeric(charCode) || this.searchValue.length >= 10) {
          event.preventDefault();
          return false;
        }
        break;

      case 'SyStudentId':
        if (!this.isNumeric(charCode) || (this.searchValue.length === 0 && charCode === 45)) { // 45 is the charCode for '-'
          event.preventDefault();
          return false;
        }
        break;
    }
    return true;
  }

  validateSearchInput(): void {
    switch(this.searchType) {
      case 'Phone Number':
        this.enableSearch = this.searchValue.length === 10;
        break;

      case 'SyStudentId':
        this.enableSearch = !isNaN(Number(this.searchValue)) && Number(this.searchValue) > 0;
        break;
    }
  }

  onSearchTypeChange(): void {
    // Update the enableSearch status based on the current input and search type
    this.validateSearchInput();
  }

  isNumeric(charCode: number): boolean {
    return charCode >= 48 && charCode <= 57; // 48-57 are charCodes for 0-9
  }

  showSearchDialog(): void {
    const dialog = document.getElementById('searchDialog');
    if (dialog) {
      this.searchValue = '';
      this.searchResults = [];
      this.searchErrorMessage = null;
      (<HTMLDialogElement>dialog).showModal();
      const searchInputBox = document.getElementById('searchInputBox');
      searchInputBox?.focus();
    }
  }

  formatPhoneNumber(phoneInfo?: PhoneInfo, phoneNumber?: string | null): string {
    let formattedPhoneNumber: string = '';

    let phoneFormatter;

    if (phoneInfo) {
      phoneFormatter = phoneInfo?.phone && parsePhoneNumber(phoneInfo.phone, 'US');
    } else if (phoneNumber) {
      phoneFormatter = parsePhoneNumber(phoneNumber, 'US');
    }

    if (phoneFormatter) {
      formattedPhoneNumber = phoneFormatter.formatNational();
    }

    return formattedPhoneNumber;
  }

  getSearchResultsClass(student: StudentInfo): string {
    let classString = '';

    if (student.phone?.isSmsDNC) {
      classString = 'dnc';
    } else if (this.allowedSchoolStatuses.length > 0 && student.schoolStatus && !this.validateSchoolStatus(student.schoolStatus)) {
      classString = 'school-status-not-allowed';
    } else if (this.searchType === 'Phone Number' && student.phone?.isPreferred) {
      classString = 'preferred';
    }

    return classString;

  }

  searchItemSelected(index: number): void {
    const conversationIndex = this.getConversationIndex(this.searchResults[index]);

    if (conversationIndex !== -1) {
      this.openConversation(conversationIndex);
    } else {
      this.displaySelectedStudent(this.searchResults[index]);
    }

    const dialog = document.getElementById('searchDialog');
    if (dialog) {
      (<HTMLDialogElement>dialog).close();
    }
  }

  getConversationIndex(student: StudentInfo): number {
    if (student.syStudentId) {
      return this.conversations.findIndex(conv => conv.syStudentId === student.syStudentId && conv.phoneNumber === student.phone?.phone);
    } else {
      return this.conversations.findIndex(conv => conv.phoneNumber === student.phone?.phone);
    }
  }

  displaySelectedStudent(student: StudentInfo): void {
    this.getUserInfo();
    this.messages = [];
    this.filteredMessages = [];
    this.activeConversationIndex = NO_CONVERSATION_SELECTED;
    this.setActiveStudent(student);
    this.getMessages().subscribe(_ => {
      this.initFiltersAndSetFocus();
    });
  }

  subscribeToNotifications(): void {
    const emailTopic = 'conversations.emails';
    this.emailTopicSubscription = this.genesysCloudService.subscribeToTopic(emailTopic).subscribe(notification => {
      let validProviders = ['GenesysCommService'];
      if(notification?.eventBody?.participants?.some((participant: Models.EmailMediaParticipant) => participant.provider 
      && validProviders.includes(participant.provider)))  {
        this.handleEmailNotification(notification);
      } 
    });
  }

  private handleEmailNotification(notification: any): void {
    const studentIndex = notification.eventBody.participants.findIndex((participant: any) => participant.purpose === 'customer');
    const agentIndex = notification.eventBody.participants.findLastIndex((participant: any) => participant.user?.id == this.genesysUserId);

    this.getConversationHistory(true).subscribe();

    if (studentIndex !== -1 && studentIndex !== undefined) {
      this.handleStudentNotification(studentIndex, notification);
    }

    if (agentIndex !== -1 && agentIndex !== undefined && notification.eventBody.participants[agentIndex].state === 'connected') {
      this.handleAgentNotification(notification);
    }
  }

  private handleStudentNotification(studentIndex: number, notification: any): void {
    if (this.isSameStudent(notification, studentIndex)) {
      this.getMessages(true).subscribe(_ => {
        this.getCaps(true);
      });
    } else {
      this.handleNewMessageNotification(studentIndex, notification);
    }
  }

  private isSameStudent(notification: any, studentIndex: number): boolean {
    const notificationPhoneNumber = notification.eventBody.participants[studentIndex].attributes.PhoneNumber;
    const notificationSyStudentId = notification.eventBody.participants[studentIndex].attributes.SyStudentId;

    return (
      this.activeStudent.phone?.phone === notificationPhoneNumber &&
      (!this.activeStudent.syStudentId || (this.activeStudent.syStudentId === +notificationSyStudentId))
    );
  }

  private handleNewMessageNotification(studentIndex: number, notification: any): void {
    const agentIndex = notification.eventBody.participants.findLastIndex((participant: any) => participant.user?.id == this.genesysUserId);
    if (agentIndex !== -1 && notification.eventBody.participants[agentIndex].state !== 'connected') return;

    const notificationPhoneNumber = notification.eventBody.participants[studentIndex].attributes.PhoneNumber;
    const conversationIndex = this.conversations.findIndex(conv => conv.phoneNumber === notificationPhoneNumber);

    if (conversationIndex !== -1) {
      const toastHeader = `New Message From ${this.formatPhoneNumber(undefined, notificationPhoneNumber)}`;

      if (this.toastService.toasts.findIndex(toast => toast.header === toastHeader) === -1) {
        let body = 'Message received from ';
        if (notification.eventBody.participants[studentIndex].attributes.FirstName && notification.eventBody.participants[studentIndex].attributes.LastName) {
          body += notification.eventBody.participants[studentIndex].attributes.FirstName+ ' ' + notification.eventBody.participants[studentIndex].attributes.LastName;
        } else {
          body += notificationPhoneNumber;
        }
        this.toastService.show({
          header: toastHeader,
          body: body,
          classname: 'bg-primary text-light'
        });

        if(notification.eventBody.participants[studentIndex].attributes.SyStudentId) {
          this.loadSpecifiedStudent(notification.eventBody.participants[studentIndex].attributes.SyStudentId);
        }
      }
    }
  }

  private handleAgentNotification(notification: any): void {
    const agentIndex = notification.eventBody.participants.findLastIndex((participant: any) => participant.user?.id == this.genesysUserId);
    const studentIndex = notification.eventBody.participants.findIndex((participant: any) => participant.purpose === 'customer');
    const agentParticipant = notification.eventBody.participants[agentIndex] as Models.EmailMediaParticipant;
    const agentConnectedTime = new Date(agentParticipant.connectedTime!).getTime();
    const studentParticipant = notification.eventBody.participants[studentIndex] as Models.EmailMediaParticipant;
    const studentConnectedTime = new Date(studentParticipant.connectedTime!).getTime();
    const rollOverSecond = studentParticipant.attributes != null ? Number(studentParticipant.attributes['RollOverSecond']) : Number.MAX_SAFE_INTEGER;
    const conversationId = notification.eventBody.id;
    // Calculate the difference in seconds
    const connectedTimeDiffSeconds = Math.floor((agentConnectedTime - studentConnectedTime) / 1000);
    const req: SmsPickUpRequested = {
      conversationId: conversationId,
      user: '',
      isRolledOver: connectedTimeDiffSeconds > rollOverSecond,
      messageId : studentParticipant.attributes!['MessageId'],
    };
    this.smsService.apiSmsPickedUpPost(req).pipe(
      catchError(error => {
        console.error(`apiSmsPickedUpPost error: ${JSON.stringify(error)}`);
        this.toastService.show({ body: 'Server error on picking up SMS', classname: 'bg-danger text-light', delay: 5000, header: 'SMS pickup Failed' });
        return [];
      })
    ).subscribe(resp => {
      if (resp.error) {
        console.error(`apiSmsPickedUpPost error: ${JSON.stringify(resp.error)}`);
        this.toastService.show({ body: 'Server response error on picking up SMS', classname: 'bg-danger text-light', delay: 5000, header: 'SMS pickup Failed' });
      } else {
        this.getActiveConversationId();
      }
    });
  }

  onPaste(event: ClipboardEvent): void {
    this.phoneService.onPaste(event);
  }

}
